import type {Editor, Range} from "@tiptap/core";
import {Fragment, Slice} from "@tiptap/pm/model";
import type {Node as ProseMirrorNode} from "@tiptap/pm/model";
import {NodeSelection, Selection} from "@tiptap/pm/state";
import type {PrintoutBlockDescriptor} from "../blocks/blockRegistry";
import {findPrintoutRegion} from "../blocks/headerFooter/printoutRegionOptions";
import type {PrintoutRegionKind} from "../blocks/headerFooter/printoutRegionOptions";
import {
    resolvePrintoutBlockDropTarget,
    resolvePrintoutBlockEscapePosition,
    type PrintoutDropCoords,
} from "./dnd/printoutDropPosition";

export const createPrintoutBlockInsertSlice = (node: ProseMirrorNode): Slice => new Slice(
    Fragment.fromArray([node, node.type.schema.nodes.paragraph.create()]),
    0,
    0,
);

const insertPrintoutBlock = (
    editor: Editor,
    block: PrintoutBlockDescriptor,
    node: ProseMirrorNode,
    position: number | Range,
): boolean => {
    if (!editor.isEditable) return false;

    return editor.chain().focus().command(({commands, tr}) => {
        const firstInsertStep = tr.mapping.maps.length;

        // The destination is an insertion range, never a temporary text selection at a block gap.
        if (!commands.insertContentAt(position, createPrintoutBlockInsertSlice(node).content)) return false;

        if (block.selectContentOnInsert) {
            let insertedPosition: number | undefined;

            // Inserting at a caret can split a paragraph. Read the inserted node's actual position.
            tr.mapping.maps.slice(firstInsertStep).forEach(map => {
                map.forEach((_from, _to, newFrom, newTo) => {
                    tr.doc.nodesBetween(newFrom, newTo, (candidate, pos) => {
                        if (insertedPosition !== undefined) return false;
                        if (!candidate.eq(node)) return true;

                        insertedPosition = pos;
                        return false;
                    });
                });
            });

            if (insertedPosition !== undefined) {
                tr.setSelection(Selection.near(tr.doc.resolve(insertedPosition + 1), 1));
            }
        }

        tr.scrollIntoView();
        return true;
    }).run();
};

export const insertPrintoutBlockAtSelection = (editor: Editor, block: PrintoutBlockDescriptor): boolean => {
    const node = block.createNode(editor.schema);

    if (!node) return false;

    const {selection} = editor.state;
    const escape = resolvePrintoutBlockEscapePosition(selection.$to, node.type);
    // Preserve selected nodes; a text selection retains the usual replace-at-caret behavior.
    const position = escape ?? (selection instanceof NodeSelection
        ? selection.to
        : {from: selection.from, to: selection.to});

    return insertPrintoutBlock(editor, block, node, position);
};

export const insertPrintoutBlockAtCoords = (
    editor: Editor,
    block: PrintoutBlockDescriptor,
    coords: PrintoutDropCoords,
): boolean => {
    const node = block.createNode(editor.schema);

    if (!node) return false;

    const target = resolvePrintoutBlockDropTarget(editor.view, createPrintoutBlockInsertSlice(node), coords);

    return target !== null && insertPrintoutBlock(editor, block, node, target);
};

export const insertPrintoutRegion = (editor: Editor, kind: PrintoutRegionKind): boolean => {
    if (!editor.isEditable || findPrintoutRegion(editor.state.doc, kind)) return false;

    const chain = kind === "header"
        ? editor.chain().focus().setPrintoutHeader()
        : editor.chain().focus().setPrintoutFooter();

    return chain.command(({dispatch, tr}) => {
        const region = findPrintoutRegion(tr.doc, kind);

        if (!region) return false;
        if (dispatch) tr.setSelection(Selection.near(tr.doc.resolve(region.pos + 1), 1));

        return true;
    }).run();
};
